import { Button, cn, Input, Label, Modal } from '@crm/ui';
import { ImagePlus, Trash2, Upload, ZoomIn } from 'lucide-react';
import { useRef, useState } from 'react';
import { ConfirmDialog } from '@/components';
import { reduzirImagem } from '@/components/photo-field';

interface PhotoSlotsProps {
  /** As fotos candidatas. */
  photos: string[];
  onChange: (photos: string[]) => void;
  size?: 'sm' | 'lg' | 'xl';
}

/**
 * As fotos candidatas de uma categoria ou servico, lado a lado, com o botao
 * de adicionar sempre depois da ultima. Cada uma abre em zoom ao clicar;
 * excluir e o unico jeito de tirar, trocar e so excluir e adicionar de novo.
 * A foto da categoria pesa mais na lista, por isso e o dobro do servico.
 */
export function PhotoSlots({ photos, onChange, size = 'lg' }: PhotoSlotsProps) {
  const lado = size === 'xl' ? 'h-40 w-40' : size === 'lg' ? 'h-20 w-20' : 'h-11 w-11';
  const [adicionando, setAdicionando] = useState(false);

  function remover(indice: number) {
    onChange(photos.filter((_, item) => item !== indice));
  }

  return (
    <div className="flex flex-wrap gap-2">
      {photos.map((foto, indice) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a lista so cresce/encolhe pelo fim
        <PhotoSlot key={indice} foto={foto} lado={lado} onRemove={() => remover(indice)} />
      ))}

      <button
        type="button"
        onClick={() => setAdicionando(true)}
        className={cn(
          'flex shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary',
          lado,
        )}
      >
        <ImagePlus className="h-5 w-5" />
        {size !== 'sm' ? <span className="text-[11px]">Adicionar foto</span> : null}
      </button>

      {adicionando ? (
        <AddPhotoModal
          onClose={() => setAdicionando(false)}
          onAdd={(foto) => {
            onChange([...photos, foto]);
            setAdicionando(false);
          }}
        />
      ) : null}
    </div>
  );
}

export function AddPhotoModal({
  onClose,
  onAdd,
  title = 'Adicionar foto',
}: {
  onClose: () => void;
  onAdd: (foto: string) => void;
  title?: string;
}) {
  const [url, setUrl] = useState('');
  const [arrastando, setArrastando] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function usarArquivo(file: File | undefined) {
    if (!file) return;
    onAdd(await reduzirImagem(file));
  }

  return (
    <Modal open onClose={onClose} title={title} size="small">
      <div className="space-y-4">
        {/* biome-ignore lint/a11y/noStaticElementInteractions: area de arrastar - clicar e teclado usam o botao dentro dela */}
        <div
          onDragOver={(event) => {
            event.preventDefault();
            setArrastando(true);
          }}
          onDragLeave={() => setArrastando(false)}
          onDrop={(event) => {
            event.preventDefault();
            setArrastando(false);
            void usarArquivo(event.dataTransfer.files?.[0]);
          }}
          className={cn(
            'flex flex-col items-center gap-2 rounded-card border border-dashed p-6 text-center transition-colors',
            arrastando ? 'border-primary bg-primary/5' : 'border-border',
          )}
        >
          <Upload className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Arraste uma imagem ou</p>
          <Button type="button" variant="outline" onClick={() => inputRef.current?.click()}>
            Escolher arquivo
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={(event) => {
              void usarArquivo(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
        </div>

        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="h-px flex-1 bg-border" />
          ou cole o endereco de uma imagem
          <span className="h-px flex-1 bg-border" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="foto-url">Endereco da imagem</Label>
          <Input
            id="foto-url"
            placeholder="https://..."
            value={url}
            onChange={(event) => setUrl(event.target.value)}
          />

          {url.trim() ? (
            <img
              src={url.trim()}
              alt=""
              className="h-32 w-full rounded-card border border-border object-cover"
            />
          ) : null}

          <Button
            type="button"
            className="w-full"
            disabled={!url.trim()}
            onClick={() => onAdd(url.trim())}
          >
            Usar esta foto
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function PhotoSlot({ foto, lado, onRemove }: { foto: string; lado: string; onRemove: () => void }) {
  const [ampliada, setAmpliada] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setAmpliada(true)}
        className={cn(
          'group relative shrink-0 overflow-hidden rounded-lg border border-border',
          lado,
        )}
        aria-label="Ver foto ampliada"
      >
        <img src={foto} alt="" className="h-full w-full object-cover" />
        <span className="absolute inset-0 flex items-center justify-center bg-black/0 text-transparent transition-colors group-hover:bg-black/30 group-hover:text-white">
          <ZoomIn className="h-5 w-5" />
        </span>
      </button>

      {ampliada ? (
        <Modal open onClose={() => setAmpliada(false)} title="Foto" size="medium">
          <img src={foto} alt="" className="w-full rounded-lg object-cover" />
          <div className="mt-4 flex justify-center">
            <Button variant="outline" onClick={() => setConfirmando(true)}>
              <Trash2 className="h-4 w-4" />
              Excluir foto
            </Button>
          </div>
        </Modal>
      ) : null}

      <ConfirmDialog
        open={confirmando}
        title="Excluir foto"
        message="A foto sai das opcoes desta categoria ou servico."
        confirmLabel="Excluir"
        onClose={() => setConfirmando(false)}
        onConfirm={() => {
          setConfirmando(false);
          setAmpliada(false);
          onRemove();
        }}
      />
    </>
  );
}
